import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonContent, Platform, PopoverController } from '@ionic/angular';
import { SelectListComponent } from 'src/app/components/select-list/select-list.component';
import { ApiService } from 'src/app/services/api.service';
import { GlobalDataService } from 'src/app/services/global-data.service';
import { JsonLdService } from 'src/app/services/json-ld.service';
import { SeoService } from 'src/app/services/seo.service';
import { CategoryItem } from 'src/app/types/category';
import { PostItem, PostListingData } from 'src/app/types/post';
import { SEOSchema } from 'src/app/types/seo';
import { removeTags } from 'src/app/utils/string-transform';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-events-listing',
  templateUrl: './events-listing.page.html',
  styleUrls: ['./events-listing.page.scss'],
})
export class EventsListingPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  lastItem;
  amountPerPage: number = 6;
  xhrInProgress: boolean = false;
  hasMoreItems: boolean = true;
  selectedCategory: CategoryItem;
  featuredItem: PostItem;
  sliceStart: number = 0;

  data: PostListingData = {
    title: 'Events',
    excerpt:
      environment.environment_name == 'capegate'
        ? 'Capegate Shopping Centre has a wide selection of unique stores and world-class events and entertainment.'
        : environment.environment_name == 'clearwater'
        ? 'Our events calendar is always busy with something special for you and the family to enjoy. From keeping the kids and family busy to centre-wide sales and fitness events. Check out our calendar below to keep a close eye on upcoming exciting events to look forward to.'
        : environment.environment_name == 'somerset'
        ? 'Our events calendar is always busy with something special for you to enjoy. Check out our calendar below to keep a close eye on upcoming exciting events.'
        : environment.environment_name == 'glen'
        ? 'Our events calendar is always busy with something special for you to enjoy. From annual school holiday events to exclusive fashion events.'
        : environment.environment_name == 'hydepark'
        ? 'Our events calendar is always busy with something special for you to enjoy. Check out our calendar below to keep a close eye on upcoming exciting events.'
        : environment.environment_name == 'rosebank'
        ? "Rosebank Mall hosts exciting events all year long, from markets to trendy roof top events to in-store experiences. Make sure you don't miss out!"
        : environment.environment_name == 'woodlands'
        ? 'Our events calendar is always busy with something special for you to enjoy. From annual school holiday events to exclusive fashion events.We have it all.'
        : "Our events calendar is always busy with something special for you to enjoy. From annual school holiday events to keep the kids busy, to exclusive fashion events highlighting the hottest seasonal trends, we have it all. Our regular kid's events turn into a hub of activity, entertaining our members with a variety of arts and crafts activities. Check out our calendar below to keep a close eye on upcoming exciting events to look forward to.",
  };

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    name: environment.environment_title,
    url: environment.environment_url,
  };

  constructor(
    private platform: Platform,
    private globalData: GlobalDataService,
    private seo: SeoService,
    private API: ApiService,
    private route: ActivatedRoute,
    public popoverController: PopoverController,
    private jsonLdService: JsonLdService
  ) {
    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
    });
  }

  /**
   * Fetch data according to page & filters
   */
  fetchData() {
    this.API.GET(
      `public/events/${environment.mall_id}?featured=1&amount=${
        this.amountPerPage
      }${
        this.lastItem
          ? '&lastSlug=' +
            this.lastItem.slug +
            '&lastPublishedDate=' +
            this.lastItem.publishedAt
          : ''
      }${
        this.selectedCategory ? '&category=' + this.selectedCategory.slug : ''
      }`
    ).subscribe({
      next: (response) => {
        if (!this.data?.items || this.data?.items.length < 1) {
          let items = response.results;
          if (response.results[0]?.featured) {
            this.featuredItem = response.results[0];
            this.sliceStart = 1;
          } else {
            this.featuredItem = null;
            this.sliceStart = 0;
          }

          this.data = {
            ...this.data,
            categories: response.categories,
            items: items,
          };
        } else {
          const extendedItems = this.data.items.concat(response.results);
          this.data.items = extendedItems;
        }

        if (this.selectedCategory?.slug && !this.selectedCategory?.title) {
          this.selectedCategory = response.categories.filter((category) => {
            return category.slug === this.selectedCategory.slug;
          })[0];
        }

        if (this.selectedCategory) this.setCategorySeo();

        this.hasMoreItems = !(response.results.length < this.amountPerPage);

        this.xhrInProgress = false;
      },
      error: (response) => {},
    });
  }

  /**
   * Load more results
   */
  loadMore() {
    if (!this.xhrInProgress) {
      this.xhrInProgress = true;

      this.lastItem = this.data.items[this.data.items.length - 1];

      this.fetchData();
    }
  }

  /**
   * Reset category filter
   */
  resetCategories() {
    delete this.data.items;
    this.selectedCategory = null;
    this.lastItem = null;
    this.fetchData();
    window?.history.pushState({}, '', '/events');

    // Set default Mall SEO data
    this.setMallSeo(this.globalData.getGlobalData());
  }

  /**
   * Present category filter
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: SelectListComponent,
      componentProps: {
        data: this.data.categories,
      },
      event: ev,
      translucent: true,
    });

    popover.onWillDismiss().then((popoverData) => {
      if (popoverData.data === 'all') {
        this.resetCategories();
      } else {
        const filterCategory = {
          title: popoverData.data?.title,
          slug: popoverData.data?.slug,
          seoTitle: popoverData.data?.seoTitle,
          seoDescription: popoverData.data?.seoDescription,
        };
        if (popoverData.data && this.selectedCategory != filterCategory) {
          this.selectedCategory = filterCategory;

          if (this.selectedCategory?.slug) {
            delete this.data.items;
            delete this.lastItem;
            this.selectedCategory = popoverData.data;
            this.fetchData();
            window?.history.pushState(
              {},
              '',
              `/events/category/${this.selectedCategory.slug}`
            );
          }
        }
      }
    });

    return await popover.present();
  }

  ngOnInit() {
    const initialMallData = this.globalData.getGlobalData();

    this.route.data.subscribe((response: any) => {
      let items = response.data.results;
      if (response.data.results[0]?.featured) {
        this.featuredItem = response.data.results[0];
        this.sliceStart = 1;
      }

      this.data = {
        ...this.data,
        categories: response.data.categories,
        items: items,
      };

      this.hasMoreItems = !(response.data.results.length < this.amountPerPage);

      let categorySlug = this.route.snapshot.paramMap.get('categorySlug');
      if (categorySlug) {
        this.selectedCategory = {
          slug: categorySlug,
        };
      } else {
        this.selectedCategory = null;
      }

      if (this.selectedCategory?.slug && !this.selectedCategory?.title) {
        for (let i = 0; i < this.data.categories.length; i++) {
          if (this.data.categories[i].slug === this.selectedCategory.slug) {
            this.selectedCategory = this.data.categories[i];
            break;
          } else if (this.data.categories[i].children) {
            for (let k = 0; k < this.data.categories[i].children.length; k++) {
              if (
                this.data.categories[i].children[k].slug ===
                this.selectedCategory.slug
              ) {
                this.selectedCategory = this.data.categories[i].children[k];
                break;
              }
            }
          }
        }
      }

      // Set SEO data
      if (this.selectedCategory) {
        this.setCategorySeo();
      } else {
        this.setMallSeo(initialMallData);
      }
    });
  }

  setMallSeo(mallData) {
    // Set Meta & Social tags
    if (!this.cordova) {
      let schemaData = { ...this.schema };

      if (this.data?.items?.length > 0) {
        let itemListElement = [];

        for (let index = 0; index < this.data.items.length; index++) {
          itemListElement.push({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Events',
              name: this.data.items[index].title,
              image: this.data.items[index].featuredImage,
              brand: {
                logo: this.data.items[index].featuredImage,
                name: this.data.items[index].title,
              },
            },
          });
        }

        schemaData = {
          ...schemaData,
          numberOfItems: this.data.items.length,
          itemListElement: itemListElement,
        };
      }

      // Title tag
      const seoTitle =
        'Events | ' + environment.environment_title + ' Shopping Centre';
      this.seo.setTitle(seoTitle);
      schemaData = {
        ...schemaData,
        name: seoTitle,
      };

      // Description tag
      const seoData = {
        title: seoTitle,
        description: removeTags(this.data.excerpt),
        keywords: mallData.general.metaKeywords + ' events',
        socialImage: mallData.social.openGraphImage,
      };
      this.seo.setMeta(seoData);
      this.seo.setSEOMeta(seoData);
      schemaData = {
        ...schemaData,
        description: seoData.description,
      };

      this.schema = { ...this.schema, ...schemaData };
      this.jsonLdService.insertSchema(this.schema);
    }
  }

  setCategorySeo() {
    let schemaData = { ...this.schema };

    const seoData = {
      title:
        'Events | ' +
        (this.selectedCategory.seoTitle &&
        this.selectedCategory.seoTitle.length > 0
          ? this.selectedCategory.seoTitle
          : this.selectedCategory.title) +
        ' | ' +
        environment.environment_title +
        ' Shopping Centre',
      description: this.selectedCategory.seoDescription,
    };
    this.seo.setTitle(seoData.title);
    schemaData = {
      ...schemaData,
      name: seoData.title,
    };
    this.seo.setMeta(seoData);
    this.seo.setSEOMeta(seoData);
    this.schema = { ...this.schema, ...schemaData };

    this.jsonLdService.insertSchema(this.schema);
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }
}
